// API Service for Analytics Backend
import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3010/api',
  timeout: 480000, // 8 minutes timeout for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API Methods
export const analyticsAPI = {
  // Execute a query with optional sessionId, conversation history, and model provider
  async executeQuery(query, sessionId = null, conversationHistory = [], modelProvider = 'claude') {
    try {
      const requestBody = { 
        query,
        sessionId: sessionId || null,
        conversationHistory: conversationHistory || [],
        modelProvider: modelProvider || 'claude' // DEFAULT: Claude (OpenAI disabled)
      };
      const response = await api.post('/analytics/query', requestBody);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to execute query',
        details: error.response?.data,
      };
    }
  },

  // Get schema information
  async getSchema() {
    try {
      const response = await api.get('/analytics/schema');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get schema',
      };
    }
  },

  // Introspect schema
  async introspectSchema() {
    try {
      const response = await api.get('/analytics/introspect');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to introspect schema',
      };
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await api.get('/analytics/health');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Health check failed',
      };
    }
  },

  // Get smart suggestions based on data
  async getSuggestions(count = 10) {
    try {
      const response = await api.get(`/analytics/suggestions?count=${count}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to get suggestions',
        suggestions: [
          'Show me all tables',
          'What data is available?',
          'List all columns',
          'Get schema information',
          'Show sample data',
        ],
      };
    }
  },

  async listSessions(limit = 50) {
    try {
      const response = await api.get(`/chat-sessions?limit=${limit}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to list sessions',
      };
    }
  },

  async createSession(initialPrompt = '') {
    try {
      const response = await api.post('/chat-sessions', { initialPrompt });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to create session',
      };
    }
  },

  async getSession(sessionId) {
    try {
      const response = await api.get(`/chat-sessions/${sessionId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch session',
      };
    }
  },

  async deleteSession(sessionId) {
    try {
      const response = await api.delete(`/chat-sessions/${sessionId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete session',
      };
    }
  },

  // Upload Excel file – adds to current data and rebuilds dictionary
  async uploadExcel(file, onUploadProgress) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/analytics/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes for large files
        onUploadProgress,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Upload failed',
        details: error.response?.data,
      };
    }
  },

  async listUploadedFiles() {
    try {
      const response = await api.get('/analytics/files');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to list uploaded files',
      };
    }
  },

  async deleteUploadedFile(fileName) {
    try {
      const response = await api.delete(`/analytics/files/${encodeURIComponent(fileName)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete uploaded file',
        details: error.response?.data,
      };
    }
  },

  async updateUserMessage(sessionId, messageIndex, content) {
    try {
      const response = await api.patch(
        `/chat-sessions/${sessionId}/messages/${messageIndex}`,
        { content },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update message',
      };
    }
  },
};

export default api;

